import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { maskCEP, maskPhone, maskCNPJ } from '../utils/formatters';
import SlotInput from './SlotInput';

interface CompanySubscriptionEditModalProps {
  subscriptionId: number;
  companyName: string;
  onClose: () => void;
  onSave: () => void;
}

const CompanySubscriptionEditModal: React.FC<CompanySubscriptionEditModalProps> = ({
  subscriptionId,
  companyName,
  onClose,
  onSave
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    closing_day: '',
    interval_months: '1',
    payment_method: '',
    mrr_value: '',
    is_pre_paid: false,
    candidates_database_role: '0',
    candidates_database_alerts_per_month: '0',
    candidates_database_contacts: '0',
    display_contacts_listing_days: '30',
    caesar_slots: '-1',
    opportunities_slots: '-1',
    recruiters_slots: '-1',
    highlight_slots: '-1',
    confidentials: '-1',
    billing_cnpj: '',
    billing_name: '',
    billing_email: '',
    billing_phone: '',
    billing_info: '',
    billing_zip: '',
    billing_neighborhood: '',
    billing_address1: '',
    billing_number: '',
    billing_address2: '',
    billing_city: '',
    billing_state: '',
    billing_simples_nacional: false
  });

  useEffect(() => {
    loadData();
  }, [subscriptionId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchSubscription(), fetchCompanyData()]);
    setLoading(false);
  };

  const fetchCompanyData = async () => {
    try {
      const subResponse = await api.get(`/companies/subscriptions/${subscriptionId}`);
      const companyId = subResponse.data.company_id;
      
      const companyResponse = await api.get(`/companies/${companyId}`);
      const company = companyResponse.data;
      
      if (company && company.billing) {
        const billing = company.billing;
        
        setFormData(prev => ({
          ...prev,
          billing_cnpj: prev.billing_cnpj || maskCNPJ(billing.cnpj || ''),
          billing_name: prev.billing_name || billing.name || '',
          billing_email: prev.billing_email || billing.email || '',
          billing_phone: prev.billing_phone || maskPhone(billing.phone || ''),
          billing_info: prev.billing_info || billing.info || '',
          billing_zip: prev.billing_zip || maskCEP(billing.zip || ''),
          billing_neighborhood: prev.billing_neighborhood || billing.neighborhood || '',
          billing_address1: prev.billing_address1 || billing.address1 || '',
          billing_number: prev.billing_number || billing.number || '',
          billing_address2: prev.billing_address2 || billing.address2 || '',
          billing_city: prev.billing_city || billing.city || '',
          billing_state: prev.billing_state || billing.state || '',
          billing_simples_nacional: prev.billing_simples_nacional || billing.simples_nacional === 1
        }));
      }
    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await api.get(`/companies/subscriptions/${subscriptionId}`);
      const sub = response.data;
      setFormData(prev => ({
        ...prev,
        closing_day: sub.closing_day?.toString() || '',
        interval_months: sub.interval_months?.toString() || '1',
        payment_method: sub.payment_method || '',
        mrr_value: sub.mrr_value?.toString() || '',
        is_pre_paid: sub.is_pre_paid === 1,
        candidates_database_role: sub.candidates_database_role?.toString() || '0',
        candidates_database_alerts_per_month: sub.candidates_database_alerts_per_month?.toString() || '0',
        candidates_database_contacts: sub.candidates_database_contacts?.toString() || '0',
        display_contacts_listing_days: sub.display_contacts_listing_days?.toString() || '30',
        caesar_slots: sub.caesar_slots?.toString() || '-1',
        opportunities_slots: sub.opportunities_slots?.toString() || '-1',
        recruiters_slots: sub.recruiters_slots?.toString() || '-1',
        highlight_slots: sub.highlight_slots?.toString() || '-1',
        confidentials: sub.confidentials?.toString() || '-1',
        billing_cnpj: maskCNPJ(sub.billing_cnpj || prev.billing_cnpj),
        billing_name: sub.billing_name || prev.billing_name,
        billing_email: sub.billing_email || prev.billing_email,
        billing_phone: maskPhone(sub.billing_phone || prev.billing_phone),
        billing_info: sub.billing_info || prev.billing_info,
        billing_zip: maskCEP(sub.billing_zip || prev.billing_zip),
        billing_neighborhood: sub.billing_neighborhood || prev.billing_neighborhood,
        billing_address1: sub.billing_address1 || prev.billing_address1,
        billing_number: sub.billing_number || prev.billing_number,
        billing_address2: sub.billing_address2 || prev.billing_address2,
        billing_city: sub.billing_city || prev.billing_city,
        billing_state: sub.billing_state || prev.billing_state,
        billing_simples_nacional: sub.billing_simples_nacional === 1 || prev.billing_simples_nacional
      }));
    } catch (error) {
      toast.error('Erro ao carregar assinatura');
    }
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, billing_phone: maskPhone(value) }));
  };

  const handleCEPChange = (value: string) => {
    setFormData(prev => ({ ...prev, billing_zip: maskCEP(value) }));
  };

  const handleCNPJChange = (value: string) => {
    setFormData(prev => ({ ...prev, billing_cnpj: maskCNPJ(value) }));
  };

  const handleNumberChange = (value: string) => {
    const numbersOnly = value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, billing_number: numbersOnly }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/companies/subscriptions/${subscriptionId}`, {
        ...formData,
        mrr_value: parseFloat(formData.mrr_value) || 0,
        is_pre_paid: formData.is_pre_paid ? 1 : 0,
        closing_day: parseInt(formData.closing_day) || null,
        interval_months: parseInt(formData.interval_months) || 1,
        candidates_database_role: parseInt(formData.candidates_database_role),
        candidates_database_alerts_per_month: parseInt(formData.candidates_database_alerts_per_month),
        candidates_database_contacts: parseInt(formData.candidates_database_contacts),
        display_contacts_listing_days: parseInt(formData.display_contacts_listing_days),
        caesar_slots: parseInt(formData.caesar_slots),
        opportunities_slots: parseInt(formData.opportunities_slots),
        recruiters_slots: parseInt(formData.recruiters_slots),
        highlight_slots: parseInt(formData.highlight_slots),
        confidentials: parseInt(formData.confidentials),
        billing_simples_nacional: formData.billing_simples_nacional ? 1 : 0,
        billing_phone: formData.billing_phone ? formData.billing_phone.replace(/\D/g, '') : '',
        billing_zip: formData.billing_zip ? formData.billing_zip.replace(/\D/g, '') : '',
        billing_cnpj: formData.billing_cnpj ? formData.billing_cnpj.replace(/\D/g, '') : ''
      });
      toast.success('Assinatura atualizada com sucesso');
      onSave();
    } catch (error) {
      toast.error('Erro ao atualizar assinatura');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Editar Assinatura - {companyName}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.content}>
          <div style={styles.columns}>
            <div style={styles.leftColumn}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Dados do Pagamento</h3>
                <div style={styles.formGroup}>
                  <label>Dia do vencimento</label>
                  <select
                    value={formData.closing_day}
                    onChange={(e) => setFormData({...formData, closing_day: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">Selecione</option>
                    {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Intervalo (meses)</label>
                  <select
                    value={formData.interval_months}
                    onChange={(e) => setFormData({...formData, interval_months: e.target.value})}
                    style={styles.input}
                  >
                    {Array.from({length: 24}, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Método de pagamento</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    style={styles.input}
                  >
                    <option value="">Selecione</option>
                    <option value="Iugu">Iugu</option>
                    <option value="boleto">Boleto</option>
                    <option value="credito">Crédito</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Valores</h3>
                <div style={styles.formGroup}>
                  <label>Valor bruto (MRR)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.mrr_value}
                    onChange={(e) => setFormData({...formData, mrr_value: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.is_pre_paid}
                      onChange={(e) => setFormData({...formData, is_pre_paid: e.target.checked})}
                    />
                    <span style={{marginLeft: '8px'}}>PRÉ-PAGO</span>
                  </label>
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Dados de cobrança</h3>
                <div style={styles.formGroup}>
                  <label>CNPJ</label>
                  <input 
                    type="text" 
                    value={formData.billing_cnpj} 
                    onChange={(e) => handleCNPJChange(e.target.value)} 
                    style={styles.input} 
                    maxLength={18}
                    placeholder="12.345.678/0001-90"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Nome</label>
                  <input type="text" value={formData.billing_name} onChange={(e) => setFormData({...formData, billing_name: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>Email</label>
                  <input type="email" value={formData.billing_email} onChange={(e) => setFormData({...formData, billing_email: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>Telefone</label>
                  <input 
                    type="text" 
                    value={formData.billing_phone} 
                    onChange={(e) => handlePhoneChange(e.target.value)} 
                    style={styles.input} 
                    maxLength={15}
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Info</label>
                  <input type="text" value={formData.billing_info} onChange={(e) => setFormData({...formData, billing_info: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>CEP</label>
                  <input 
                    type="text" 
                    value={formData.billing_zip} 
                    onChange={(e) => handleCEPChange(e.target.value)} 
                    style={styles.input} 
                    maxLength={9}
                    placeholder="12345-678"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Bairro</label>
                  <input type="text" value={formData.billing_neighborhood} onChange={(e) => setFormData({...formData, billing_neighborhood: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>Endereço</label>
                  <input type="text" value={formData.billing_address1} onChange={(e) => setFormData({...formData, billing_address1: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>Número</label>
                  <input 
                    type="text" 
                    value={formData.billing_number} 
                    onChange={(e) => handleNumberChange(e.target.value)} 
                    style={styles.input}
                    placeholder="123"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Complemento</label>
                  <input type="text" value={formData.billing_address2} onChange={(e) => setFormData({...formData, billing_address2: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>Cidade</label>
                  <input type="text" value={formData.billing_city} onChange={(e) => setFormData({...formData, billing_city: e.target.value})} style={styles.input} />
                </div>
                <div style={styles.formGroup}>
                  <label>Estado</label>
                  <input type="text" value={formData.billing_state} onChange={(e) => setFormData({...formData, billing_state: e.target.value})} style={styles.input} maxLength={2} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.checkboxLabel}>
                    <input type="checkbox" checked={formData.billing_simples_nacional} onChange={(e) => setFormData({...formData, billing_simples_nacional: e.target.checked})} />
                    <span style={{marginLeft: '8px'}}>Optante pelo simples nacional</span>
                  </label>
                </div>
              </div>
            </div>

            <div style={styles.rightColumn}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Banco de Talentos</h3>
                <div style={styles.formGroup}>
                  <label>Acesso</label>
                  <select
                    value={formData.candidates_database_role}
                    onChange={(e) => setFormData({...formData, candidates_database_role: e.target.value})}
                    style={styles.input}
                  >
                    <option value="2">ACESSO COMPLETO</option>
                    <option value="1">ACESSO BÁSICO</option>
                    <option value="0">SEM ACESSO</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Total de alertas</label>
                  <input
                    type="number"
                    value={formData.candidates_database_alerts_per_month}
                    onChange={(e) => setFormData({...formData, candidates_database_alerts_per_month: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Qtd de contatos abertos</label>
                  <input
                    type="number"
                    value={formData.candidates_database_contacts}
                    onChange={(e) => setFormData({...formData, candidates_database_contacts: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Tempo de listagem (dias)</label>
                  <input
                    type="number"
                    value={formData.display_contacts_listing_days}
                    onChange={(e) => setFormData({...formData, display_contacts_listing_days: e.target.value})}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Caesar</h3>
                <SlotInput
                  label="SLOTS"
                  value={parseInt(formData.caesar_slots)}
                  onChange={(value) => setFormData({...formData, caesar_slots: value.toString()})}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Vagas</h3>
                <SlotInput
                  label="Slots de Vagas"
                  value={parseInt(formData.opportunities_slots)}
                  onChange={(value) => setFormData({...formData, opportunities_slots: value.toString()})}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
                <SlotInput
                  label="Slots de Recrutadores"
                  value={parseInt(formData.recruiters_slots)}
                  onChange={(value) => setFormData({...formData, recruiters_slots: value.toString()})}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
                <SlotInput
                  label="Slots de destaques"
                  value={parseInt(formData.highlight_slots)}
                  onChange={(value) => setFormData({...formData, highlight_slots: value.toString()})}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
                <SlotInput
                  label="Qtd. Confidenciais"
                  value={parseInt(formData.confidentials)}
                  onChange={(value) => setFormData({...formData, confidentials: value.toString()})}
                  tooltip="-1 = Não incluído, 0 = Ilimitado, >0 = Quantidade específica"
                />
              </div>
            </div>
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>
              Cancelar
            </button>
            <button type="submit" disabled={saving} style={styles.saveBtn}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '20px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '20px',
    maxHeight: 'calc(90vh - 140px)',
    overflow: 'auto',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: colors.textLight,
  },
  columns: {
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
    flex: '0 0 400px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '20px',
    flex: '1',
  },
  section: {
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    padding: '16px',
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    color: colors.primary,
    borderBottom: `1px solid ${colors.border}`,
    paddingBottom: '8px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '20px',
    borderTop: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    padding: '10px 20px',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '10px 20px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
};

export default CompanySubscriptionEditModal;
